import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceNotice,
  useToast,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import { useMoveVersionMutation, useVersionMoveQuery } from '@/lib/api/discovery';
import { plural } from './discovery-logic';

/**
 * Move the engagement to a newer framework version (T-0185; rule 4,
 * Decisions 54.7 and 54.8). The engagement lead only: the header shows
 * the link to everyone, but only the lead's opens this dialog.
 *
 * The preview is computed by the API and writes nothing: the areas the
 * move adds, retires or renames, the sources rule before and after,
 * the evidence that stops counting, and coverage before and after.
 * Mount it only while open, so a reopened dialog starts clean.
 */
export function VersionMoveDialog({
  engagementId,
  frameworkName,
  from,
  to,
  open,
  onOpenChange,
}: {
  engagementId: string;
  frameworkName: string;
  from: number;
  to: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const preview = useVersionMoveQuery(engagementId, open ? to : null);
  const move = useMoveVersionMutation(engagementId);
  const { toast } = useToast();
  const p = preview.data;

  const onMove = () =>
    move.mutate(
      { to_version: to, from_version: from },
      {
        onSuccess: (r) => {
          toast({
            message: `Moved to v${r.framework_version}. Coverage was recomputed under its rules, and the move is recorded.`,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          if (
            error instanceof ApiError &&
            error.details?.['reason'] === 'discovery.version_changed'
          ) {
            void preview.refetch();
          }
        },
      },
    );

  const before = new Map((p?.coverage.before ?? []).map((c) => [c.area_id, c]));
  const after = new Map((p?.coverage.after ?? []).map((c) => [c.area_id, c]));
  const changed = [...new Set([...before.keys(), ...after.keys()])]
    .map((id) => ({ id, b: before.get(id), a: after.get(id) }))
    .filter(({ b, a }) => b?.status_label !== a?.status_label);
  const rule = p?.rules.sources_required;

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Move to ${frameworkName} v${to}`}
      description={`This engagement is on v${from}. Moving recomputes coverage under v${to}’s areas and rules. Moves only go forward, and each one is recorded.`}
      actions={
        <>
          <WorkspaceButton size="md" variant="tertiary" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!p || move.isPending}
            onClick={onMove}
          >
            {move.isPending ? 'Moving…' : `Move to v${to}`}
          </WorkspaceButton>
        </>
      }
    >
      {preview.error ? <ErrorMessage error={preview.error} /> : null}
      {!p && !preview.error ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Working out what changes…
        </div>
      ) : null}
      {p ? (
        <>
          <section>
            <h3 className="mb-1.5 text-meta font-medium uppercase tracking-wide text-ink-500">
              What changes
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-body-sm text-ink-900">
              <li>
                {p.areas.added.length
                  ? `Adds ${plural(p.areas.added.length, 'area')}: ${p.areas.added.map((a) => a.label).join(', ')}`
                  : 'Adds no areas'}
              </li>
              <li>
                {p.areas.retired.length
                  ? `Retires ${plural(p.areas.retired.length, 'area')}: ${p.areas.retired.map((a) => a.label).join(', ')}`
                  : 'Retires no areas'}
              </li>
              {p.areas.renamed.map((r) => (
                <li key={r.area_id}>
                  Renames {r.before.label} to {r.after.label}
                </li>
              ))}
              <li>
                {rule && rule.from !== rule.to
                  ? `Independent sources each area needs: ${rule.from} → ${rule.to}`
                  : `Independent sources each area needs: stays at ${rule?.to}`}
              </li>
            </ul>
          </section>
          {p.evidence_left_on_retired_areas.stops_counting > 0 ? (
            <WorkspaceNotice tone="at-risk">
              {plural(p.evidence_left_on_retired_areas.stops_counting, 'confirmed evidence item')}{' '}
              tagged only to a retired area stay in the ledger under that area and stop counting.
            </WorkspaceNotice>
          ) : null}
          <section>
            <h3 className="mb-1.5 text-meta font-medium uppercase tracking-wide text-ink-500">
              Coverage
            </h3>
            {changed.length === 0 ? (
              <p className="text-body-sm text-ink-700">Coverage stays the same in every area.</p>
            ) : (
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="text-left text-meta text-ink-500">
                    <th className="py-1 font-medium">Area</th>
                    <th className="py-1 font-medium">Now</th>
                    <th className="py-1 font-medium">After the move</th>
                  </tr>
                </thead>
                <tbody>
                  {changed.map(({ id, b, a }) => (
                    <tr key={id} className="border-t border-ink-100">
                      <td className="py-1.5 font-medium text-ink-900">{(a ?? b)?.label}</td>
                      <td className="py-1.5 text-ink-700">
                        {b?.status_label ?? 'Not in v' + p.from}
                      </td>
                      <td className="py-1.5 text-ink-900">{a?.status_label ?? 'Retired'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : null}
      {move.error ? <ErrorMessage error={move.error} /> : null}
    </WorkspaceDialog>
  );
}
